import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, UserCheck, ClipboardList, Users, Package, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  received: { en: "Received", es: "Recibido" },
  in_progress: { en: "In Progress", es: "En Proceso" },
  shipped: { en: "Shipped", es: "Enviado" },
};

export const NotificationsPanel = ({ token, language = "es" }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/notifications`, { headers: { Authorization: `Bearer ${token}` } });
        if (mounted) setData(res.data);
      } catch {
        // silent
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 30000); // poll every 30s
    return () => { mounted = false; clearInterval(t); };
  }, [token]);

  if (loading || !data) {
    return (
      <Card className="mb-6 border-stone-200" data-testid="notifications-panel-loading">
        <CardContent className="p-5 text-sm text-stone-400">
          {language === "es" ? "Cargando notificaciones..." : "Loading notifications..."}
        </CardContent>
      </Card>
    );
  }

  const total = data.total || 0;
  const pUsers = data.pending_users?.count || 0;
  const pClients = data.pending_clients?.count || 0;
  const pSurveys = data.pending_surveys?.count || 0;
  const activeOrders = data.active_orders?.items || [];

  const tt = (en, es) => (language === "es" ? es : en);

  const cards = [
    {
      key: "users",
      count: pUsers,
      label: tt("Users to approve", "Usuarios por aprobar"),
      icon: UserCheck,
      color: "amber",
      onClick: () => navigate("/admin"),
    },
    {
      key: "clients",
      count: pClients,
      label: tt("Clients to approve", "Clientes por aprobar"),
      icon: Users,
      color: "rose",
      onClick: () => navigate("/admin/clients"),
    },
    {
      key: "surveys",
      count: pSurveys,
      label: tt("Surveys pending review", "Encuestas por revisar"),
      icon: ClipboardList,
      color: "emerald",
      onClick: () => navigate("/admin?section=surveys"),
    },
  ];

  const colorMap = {
    amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", ring: "ring-amber-200", icon: "bg-amber-100 text-amber-700" },
    rose:  { bg: "bg-rose-50",  border: "border-rose-200",  text: "text-rose-700",  ring: "ring-rose-200",  icon: "bg-rose-100 text-rose-700"  },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", ring: "ring-emerald-200", icon: "bg-emerald-100 text-emerald-700" },
    indigo: { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700", ring: "ring-indigo-200", icon: "bg-indigo-100 text-indigo-700" },
  };

  return (
    <Card className="mb-6 border-stone-200 bg-gradient-to-br from-white to-stone-50" data-testid="notifications-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Bell className="w-5 h-5 text-indigo-600" />
              </div>
              {total > 0 && (
                <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs h-5 min-w-5 px-1.5 rounded-full border-2 border-white" data-testid="notifications-total-badge">
                  {total}
                </Badge>
              )}
            </div>
            <div>
              <CardTitle className="text-lg text-stone-900">
                {tt("Notifications", "Notificaciones")}
              </CardTitle>
              <p className="text-xs text-stone-500">
                {total === 0
                  ? tt("You're all caught up", "Todo al día")
                  : tt(`${total} item(s) need your attention`, `${total} elemento(s) requieren tu atención`)}
              </p>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {cards.map((c) => {
            const Icon = c.icon;
            const cm = colorMap[c.color];
            const isEmpty = c.count === 0;
            return (
              <button
                key={c.key}
                onClick={c.onClick}
                disabled={isEmpty}
                data-testid={`notif-card-${c.key}`}
                className={`text-left group rounded-xl border ${cm.border} ${cm.bg} p-4 transition-all ${
                  isEmpty ? "opacity-60 cursor-default" : "hover:ring-2 hover:" + cm.ring + " cursor-pointer"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cm.icon}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className={`text-2xl font-bold ${cm.text}`}>{c.count}</span>
                </div>
                <p className="text-sm font-medium text-stone-800 mt-3">{c.label}</p>
                {!isEmpty && (
                  <p className={`text-xs mt-1 flex items-center gap-1 ${cm.text} group-hover:underline`}>
                    {tt("View", "Ver")} <ChevronRight className="w-3 h-3" />
                  </p>
                )}
              </button>
            );
          })}
        </div>

        {/* Active orders row */}
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-indigo-600" />
              <p className="text-sm font-semibold text-indigo-900">
                {tt("Active orders (not delivered)", "Pedidos activos (no entregados)")}
              </p>
              <Badge className="bg-indigo-600 text-white" data-testid="notif-active-orders-count">
                {activeOrders.length}
              </Badge>
            </div>
            <Button size="sm" variant="ghost" onClick={() => navigate("/admin/orders")}
              className="text-indigo-600 hover:text-indigo-800" data-testid="notif-open-orders">
              {tt("View all", "Ver todos")} <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
          {activeOrders.length === 0 ? (
            <p className="text-xs text-stone-500">{tt("No orders in progress", "No hay pedidos en proceso")}</p>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-auto">
              {activeOrders.slice(0, 6).map((o) => (
                <div key={o.id} className="flex items-center justify-between text-sm bg-white rounded-lg px-3 py-2 border border-indigo-100"
                  data-testid={`notif-order-${o.tracking_number}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono font-semibold text-indigo-600 truncate">{o.tracking_number}</span>
                    <span className="text-stone-600 truncate">{o.client_name}</span>
                  </div>
                  <Badge className={
                    o.status === "in_progress" ? "bg-amber-100 text-amber-700" :
                    o.status === "shipped" ? "bg-emerald-100 text-emerald-700" :
                    "bg-stone-200 text-stone-700"
                  }>
                    {STATUS_LABELS[o.status]?.[language] || o.status}
                  </Badge>
                </div>
              ))}
              {activeOrders.length > 6 && (
                <p className="text-xs text-stone-500 text-center pt-1">
                  +{activeOrders.length - 6} {tt("more", "más")}
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationsPanel;
